import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { PieChart, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr } from '../../../components/admin/billing/shared';

const currentYM = () => {
  const d = new Date();
  return { y: d.getFullYear(), m: d.getMonth() + 1 };
};
const shiftMonth = ({ y, m }, delta) => {
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1 };
};
const monthLabel = ({ y, m }) => new Date(Date.UTC(y, m - 1, 1))
  .toLocaleString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' });

const CostCentrePnl = () => {
  const { authHeader } = useAuth();
  const [{ y, m }, setYm] = useState(currentYM());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await axios.get(`${API}/gst/reports/cost-centre-pnl`, {
        headers: authHeader(), params: { year: y, month: m },
      });
      setData(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, y, m]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  const marginColor = (pct) => {
    if (pct == null) return 'text-slate-400';
    if (pct >= 40) return 'text-emerald-700 font-semibold';
    if (pct >= 15) return 'text-emerald-600';
    if (pct >= 0) return 'text-amber-600';
    return 'text-red-600 font-semibold';
  };

  const downloadCsv = () => {
    const lines = [
      ['Head', 'Revenue', 'Expenses', 'Net', 'Margin %'],
      ...rows.map((r) => [r.head, r.revenue, r.expenses, r.net,
                          r.margin_pct == null ? '' : r.margin_pct]),
      ['TOTAL', totals.revenue, totals.expenses, totals.net,
       totals.margin_pct == null ? '' : totals.margin_pct],
    ];
    const csv = lines.map((row) => row.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cost-centre-pnl_${y}_${String(m).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-6 bg-slate-50 min-h-full space-y-4" data-testid="cost-centre-pnl-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <PieChart className="text-teal-600" size={22} />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Cost-Centre P&amp;L</h1>
            <p className="text-xs text-slate-500">Revenue minus expenses per service department. Data lives in GST invoices &amp; expenses.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/reports" className="text-xs bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-1.5 rounded-lg" data-testid="back-to-reports">← Reports hub</Link>
          <button type="button" onClick={downloadCsv} disabled={!rows.length}
            className="text-xs bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold px-3 py-1.5 rounded-lg"
            data-testid="download-csv">CSV</button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-2">
        <button type="button" onClick={() => setYm((p) => shiftMonth(p, -1))} className="p-1.5 rounded hover:bg-slate-100 text-slate-700" data-testid="prev-month">
          <ChevronLeft size={16} />
        </button>
        <div className="min-w-[160px] text-center font-semibold text-slate-800" data-testid="month-label">
          {monthLabel({ y, m })}
        </div>
        <button type="button" onClick={() => setYm((p) => shiftMonth(p, 1))} className="p-1.5 rounded hover:bg-slate-100 text-slate-700" data-testid="next-month">
          <ChevronRight size={16} />
        </button>
        <button type="button" onClick={() => setYm(currentYM())} className="ml-2 text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700" data-testid="this-month">
          This month
        </button>
      </div>

      {err && (
        <div className="border border-red-200 bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="err">{err}</div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-slate-400 flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="pnl-table">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="text-left px-4 py-2.5">Head</th>
                  <th className="text-right px-4 py-2.5">Revenue</th>
                  <th className="text-right px-4 py-2.5">Expenses</th>
                  <th className="text-right px-4 py-2.5">Net</th>
                  <th className="text-right px-4 py-2.5">Margin %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.head} data-testid={`row-${r.head.replace(/\s+/g, '-').toLowerCase()}`}>
                    <td className="px-4 py-2 text-slate-800 font-medium">{r.head}</td>
                    <td className="px-4 py-2 text-right">₹ {fmtINR(r.revenue)}</td>
                    <td className="px-4 py-2 text-right">₹ {fmtINR(r.expenses)}</td>
                    <td className={`px-4 py-2 text-right font-semibold ${r.net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      ₹ {fmtINR(r.net)}
                    </td>
                    <td className={`px-4 py-2 text-right ${marginColor(r.margin_pct)}`}>
                      {r.margin_pct == null ? '—' : `${r.margin_pct}%`}
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-100 font-semibold" data-testid="row-total">
                  <td className="px-4 py-2.5 text-slate-900">TOTAL</td>
                  <td className="px-4 py-2.5 text-right">₹ {fmtINR(totals.revenue || 0)}</td>
                  <td className="px-4 py-2.5 text-right">₹ {fmtINR(totals.expenses || 0)}</td>
                  <td className={`px-4 py-2.5 text-right ${(totals.net || 0) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                    ₹ {fmtINR(totals.net || 0)}
                  </td>
                  <td className={`px-4 py-2.5 text-right ${marginColor(totals.margin_pct)}`}>
                    {totals.margin_pct == null ? '—' : `${totals.margin_pct}%`}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CostCentrePnl;
